<template>
  <Loader v-if="isLoading" display="absolute"></Loader>
  <Alert v-if="errorMsgs.length" type="danger" :messages="errorMsgs"></Alert>

  <form
    id="form"
    ref="form"
    autocomplete="off"
    class="b-leasing-contact-form"
    @submit.prevent="onSubmitForm">
    <div class="b-leasing-contact-form__panel mb-30">
      <div class="b-leasing-contact-form__title">
        {{ dictionary.personalDetailsLabel }}
      </div>

      <div class="row">
        <!-- name -->
        <div class="col-lg-6 mb-30">
          <label for="name" class="form-label">
            {{ dictionary.nameLabel }}
          </label>
          <input
            id="name"
            v-model.trim="name"
            class="form-control"
            :class="{ 'has-error': v$.name.$error }"
            type="text"
            maxlength="30"
            @blur="v$.name.$touch" />

          <div v-if="v$.name.$error">
            <div v-if="v$.name.required.$invalid" class="error">
              {{ dictionary.nameRequired }}
            </div>
            <!-- prettier-ignore -->
            <div
              v-if="v$.name.minLength.$invalid || v$.name.maxLength.$invalid"
              class="error">
              {{ dictionary.nameLength }}
            </div>
          </div>
        </div>
        <!-- surname -->
        <div class="col-lg-6 mb-30">
          <label for="surname" class="form-label">
            {{ dictionary.surnameLabel }}
          </label>
          <input
            id="surname"
            v-model.trim="surname"
            class="form-control"
            :class="{ 'has-error': v$.surname.$error }"
            type="text"
            maxlength="40"
            @blur="v$.surname.$touch" />

          <div v-if="v$.surname.$error">
            <div v-if="v$.surname.required.$invalid" class="error">
              {{ dictionary.surnameRequired }}
            </div>
            <!-- prettier-ignore -->
            <div
              v-if="v$.surname.minLength.$invalid || v$.surname.maxLength.$invalid"
              class="error">
              {{ dictionary.surnameLength }}
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <!-- email -->
        <div class="col-lg-6 mb-30">
          <label for="email" class="form-label">
            {{ dictionary.emailLabel }}
          </label>
          <input
            id="email"
            v-model.trim="email"
            class="form-control"
            :class="{ 'has-error': v$.email.$error }"
            type="email"
            maxlength="50"
            @blur="v$.email.$touch" />

          <div v-if="v$.email.$error">
            <div v-if="v$.email.required.$invalid" class="error">
              {{ dictionary.emailRequired }}
            </div>
            <div v-if="v$.email.email.$invalid" class="error">
              {{ dictionary.emailRegex }}
            </div>
            <!-- prettier-ignore -->
            <div
              v-if="v$.email.minLength.$invalid || v$.email.maxLength.$invalid"
              class="error">
              {{ dictionary.emailLength }}
            </div>
          </div>
        </div>
        <!-- confirm email -->
        <div class="col-lg-6 mb-30">
          <label for="confirmEmail" class="form-label">
            {{ dictionary.confirmEmailLabel }}
          </label>
          <input
            id="confirmEmail"
            v-model.trim="confirmEmail"
            class="form-control"
            :class="{ 'has-error': v$.confirmEmail.$error }"
            type="email"
            maxlength="50"
            @blur="v$.confirmEmail.$touch" />

          <div v-if="v$.confirmEmail.$error">
            <div v-if="v$.confirmEmail.required.$invalid" class="error">
              {{ dictionary.confirmEmailRequired }}
            </div>
            <div v-if="v$.confirmEmail.email.$invalid" class="error">
              {{ dictionary.emailRegex }}
            </div>
            <!-- prettier-ignore -->
            <div
              v-if="v$.confirmEmail.minLength.$invalid || v$.confirmEmail.maxLength.$invalid"
              class="error">
              {{ dictionary.emailLength }}
            </div>
            <div v-if="v$.confirmEmail.sameAsPassword.$invalid" class="error">
              {{ dictionary.confirmEmailSameAs }}
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <!-- phone type -->
        <div class="col-lg-6 mb-30">
          <label for="phoneType" class="form-label">
            {{ dictionary.phoneTypeLabel }}
          </label>
          <select id="phoneType" v-model="phoneType" class="form-select">
            <option
              v-for="item in phoneTypes"
              :key="item.value"
              :value="item.value">
              {{ item.text }}
            </option>
          </select>
        </div>
        <!-- phone -->
        <div class="col-lg-6 mb-30">
          <label for="phone" class="form-label">
            {{ dictionary.phoneLabel }}
          </label>
          <div class="phone-group">
            <MultiSelect
              id="dialingCode"
              v-model="dialingCode"
              autocomplete="nope"
              :searchable="true"
              :clear-on-search="true"
              :options="dialingCodes"
              :can-deselect="false"
              :can-clear="false"
              :no-results-text="dictionary.noResultsText"
              label="text">
              <template #singlelabel="{ value }">
                <div class="multiselect-single-label">
                  <img class="multiselect-icon-currency" :src="value.icon" />
                  {{ value.text }}
                </div>
              </template>

              <template #option="{ option }">
                <img class="multiselect-icon-currency" :src="option.icon" />
                {{ option.text }}
              </template>
            </MultiSelect>

            <input
              id="phone"
              v-model.trim="phone"
              class="form-control"
              name="phone"
              type="tel"
              :class="{ 'has-error': v$.phone.$error }"
              maxlength="40"
              @blur="v$.phone.$touch" />
          </div>
          <div v-if="v$.phone.$error">
            <div v-if="v$.phone.required.$invalid" class="error">
              {{ dictionary.phoneRequired }}
            </div>
            <!-- prettier-ignore -->
            <div
                v-if="v$.phone.minLength.$invalid || v$.phone.maxLength.$invalid"
                class="error">
                {{ dictionary.phoneLength }}
              </div>
            <div v-if="v$.phone.numeric.$invalid" class="error">
              {{ dictionary.phoneNumeric }}
            </div>
          </div>
        </div>
      </div>

      <div class="b-leasing-contact-form__title">
        {{ dictionary.companyDetailsLabel }}
      </div>

      <div class="mb-30">
        <input
          id="member"
          v-model="isMember"
          type="checkbox"
          name="member"
          class="form-check-input" />
        <label for="member" class="form-check-label">
          {{ dictionary.memberLabel }}
        </label>
      </div>
      <div v-if="isMember" class="row">
        <div class="col-lg-6 mb-30">
          <label for="memberCode" class="form-label">
            {{ dictionary.memberCodeLabel }}
          </label>
          <input
            id="memberCode"
            v-model.trim="memberCode"
            class="form-control"
            type="text"
            maxlength="30" />
        </div>
      </div>

      <div v-if="isMember">
        <div class="separator mb-30" @click="toggleSupport">
          <div class="title">{{ dictionary.customerSupportLabel }}</div>
          <div class="line"></div>
          <div v-if="!isSupportOpen" class="icon">
            <i class="icon-triangle-down"></i>
          </div>
          <div v-else class="icon">
            <i class="icon-triangle-up"></i>
          </div>
        </div>

        <div
          v-if="isSupportOpen"
          class="alert alert-warning mb-40"
          role="alert">
          <div class="alert__body">
            <div class="alert__aside d-none d-lg-flex">
              <LazyImage :image="{ url: '/imgs/icons/alert-warning.svg' }" />
            </div>
            <div
              class="alert__main"
              v-html="`${dictionary.customerSupportText}`"></div>
          </div>
        </div>
      </div>

      <div v-if="isMember" class="row">
        <div class="col-lg-6 mb-30">
          <label for="brandName" class="form-label">
            {{ dictionary.brandNameLabel }}
          </label>
          <input
            id="brandName"
            v-model.trim="brandName"
            class="form-control"
            type="text"
            maxlength="30" />
        </div>

        <div class="col-lg-6 mb-30">
          <label for="customerType" class="form-label">
            {{ dictionary.customerTypeLabel }}
          </label>
          <select
            id="customerType"
            v-model="customerType"
            name="customerType"
            class="form-select">
            <option
              v-for="item in customerTypes"
              :key="item.value"
              :value="item.value">
              {{ item.text }}
            </option>
          </select>
        </div>
      </div>

      <hr />

      <div class="row">
        <div class="col-lg-6 mb-30">
          <label for="topic" class="form-label">
            {{ dictionary.topicLabel }}
          </label>
          <select id="topic" v-model="topic" name="topic" class="form-select">
            <option value="" disabled>
              {{ dictionary.topicDefaultOptionText }}
            </option>
            <option
              v-for="item in topics"
              :key="item.value"
              :value="item.value">
              {{ item.text }}
            </option>
          </select>
        </div>
      </div>

      <div class="row">
        <!-- Comments -->
        <div class="col">
          <label for="comments" class="form-label">
            {{ dictionary.commentsLabel }}
          </label>
          <textarea
            id="comments"
            v-model.trim="comments"
            name="comments"
            class="form-control mb-10"
            :class="{ 'has-error': v$.comments.$error }"
            :placeholder="dictionary.commentsPlaceholder"
            maxlength="400"
            @blur="v$.comments.$touch"></textarea>

          <div v-if="v$.comments.$error">
            <div v-if="v$.comments.maxLength.$invalid" class="error">
              {{ dictionary.commentsLength }}
            </div>
            <div v-if="v$.comments.required.$invalid" class="error">
              {{ dictionary.commentsRequired }}
            </div>
          </div>

          <!-- comments characters -->
          <div class="row">
            <div class="col d-flex justify-content-end">
              <small>
                <span v-if="commentsLength">{{ commentsLength }} /</span>
                {{ commentsMaxLength }}
                {{ dictionary.commentsCharacters }}
                {{ dictionary.commentsCharactersLabel }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-30">
      <input
        id="terms"
        v-model="terms"
        type="checkbox"
        name="terms"
        class="form-check-input"
        :class="{ 'has-error': v$.terms.$error }" />
      <label
        for="terms"
        class="form-check-label"
        v-html="dictionary.termsLabel"></label>

      <div v-if="v$.terms.$error">
        <div v-if="v$.terms.checked.$invalid" class="error">
          {{ dictionary.termsRequired }}
        </div>
      </div>
    </div>
    <div class="recaptcha">
      <div id="leasing-cf-recaptcha"></div>

      <div v-if="v$.reCaptchaResponse.$error">
        <div v-if="v$.reCaptchaResponse.required.$invalid" class="error">
          {{ dictionary.reCaptchaRequired }}
        </div>
      </div>
    </div>
    <div class="row">
      <div class="submitButton">
        <button type="submit" class="btn btn-primary">
          {{ dictionary.buttonSubmitLabel }}
        </button>
      </div>
    </div>
  </form>

  <SuccessModal
    id="modalSuccess"
    :title="response.title"
    :text="response.text" />
</template>

<script>
import axios from 'axios'
import * as lib from './../../lib/lib'
import Alert from './../components/common/Alert.vue'
import Loader from './../components/common/Loader.vue'
import LazyImage from './../components/common/LazyImage.vue'
import MultiSelect from '@vueform/multiselect'
import SuccessModal from './../components/SuccessModal.vue'
import { Modal } from 'bootstrap'
import useVuelidate from '@vuelidate/core'
import {
  email,
  maxLength,
  minLength,
  numeric,
  required,
  sameAs,
} from '@vuelidate/validators'

export default {
  components: { MultiSelect, SuccessModal, Alert, Loader, LazyImage },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      scrollOffsetY: 125,
      isLoading: false,
      errorMsgs: [],

      customerType: '',
      brandName: '',

      name: '',
      surname: '',

      email: '',
      confirmEmail: '',

      dialingCode: '',
      phone: '',
      phoneType: '',

      isMember: false,
      memberCode: '',

      isSupportOpen: false,

      topic: '',

      comments: '',
      commentsMaxLength: 400,

      terms: false,

      reCaptchaResponse: '',
      reCaptchaWidget: null,
      response: { title: '', text: '' },
      phoneTypes: [],
      dialingCodes: [],
      topics: [],
    }
  },
  validations() {
    return {
      name: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(30),
      },
      surname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
      },
      email: {
        required,
        email,
        minLength: minLength(6),
        maxLength: maxLength(50),
      },
      confirmEmail: {
        required,
        email,
        minLength: minLength(6),
        maxLength: maxLength(50),
        sameAsPassword: sameAs(this.email),
      },
      phone: {
        required,
        numeric,
        minLength: minLength(5),
        maxLength: maxLength(40),
      },
      dialingCode: {
        required,
      },
      comments: {
        required,
        maxLength: maxLength(this.commentsMaxLength),
      },
      terms: {
        checked: (value) => value === true,
      },
      reCaptchaResponse: {
        required,
      },
    }
  },
  computed: {
    commentsLength() {
      return this.comments.length
    },
  },
  mounted() {
    const { dialingCodes, phoneTypes, topics, customerTypes, response } =
      lib.getData('contactFormData')

    this.dialingCodes = dialingCodes
    this.phoneTypes = phoneTypes
    this.topics = topics
    this.customerTypes = customerTypes
    this.response = response

    document.addEventListener('recaptcha-ready', () => {
      if (this.reCaptchaWidget === null) {
        this.iniReCaptcha()
      }
    })

    this.modalSuccess = new Modal(document.getElementById('modalSuccess'))

    this.$nextTick(() => {
      this.setPreselectedOptions()
    })
  },
  methods: {
    resetForm() {
      this.$refs.form.reset()

      this.customerType =
        this.brandName =
        this.name =
        this.surname =
        this.email =
        this.confirmEmail =
        this.dialingCode =
        this.phone =
        this.phoneType =
        this.memberCode =
        this.topic =
        this.comments =
        this.reCaptchaResponse =
          ''

      this.isMember = false
      this.isSupportOpen = false
      this.terms = false

      this.setPreselectedOptions()

      this.v$.$reset()
      this.resetReCaptcha()
    },

    toggleSupport() {
      this.isSupportOpen = !this.isSupportOpen
    },

    setPreselectedOptions() {
      const entry = this.dialingCodes.find((i) => i.selected)
      this.dialingCode = entry ? entry.value : ''

      const entry2 = this.phoneTypes.find((i) => i.selected)
      this.phoneType = entry2 ? entry2.value : ''

      const entry3 = this.customerTypes.find((i) => i.selected)
      this.customerType = entry3 ? entry3.value : ''
    },

    callbackReCaptcha(response) {
      this.reCaptchaResponse = response
    },

    expiredCallbackReCaptha() {
      this.reCaptchaResponse = ''
    },

    iniReCaptcha() {
      document.getElementById('leasing-cf-recaptcha').innerHTML = ''

      const grecaptcha = window.grecaptcha
      
      if (grecaptcha && grecaptcha.render) {
        this.reCaptchaWidget = grecaptcha.render('leasing-cf-recaptcha', {
          sitekey: this.configuration.reCaptchaPublicKey,
          callback: this.callbackReCaptcha,
          'expired-callback': this.expiredCallbackReCaptha,
        })
      }
    },

    resetReCaptcha() {
      this.reCaptchaResponse = ''
      window.grecaptcha.reset(this.reCaptchaWidget)
    },

    onSubmitForm() {
      this.v$.$validate()

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          lib.scrollToValidation(this.scrollOffsetY)
        })
        return false
      }

      this.isLoading = true
      const formData = new FormData()

      formData.append('Name', this.name)
      formData.append('Surname', this.surname)
      formData.append('Email', this.email)
      formData.append('ConfirmEmail', this.confirmEmail)
      formData.append('AcceptTerms', this.terms)
      formData.append('g-recaptcha-response', this.reCaptchaResponse)
      formData.append('Phone', this.dialingCode + this.phone)
      formData.append('PhoneType', this.phoneType)
      formData.append('PhoneDialingCode', this.dialingCode)
      formData.append('CDP', this.memberCode)
      formData.append('Subject', this.topic)
      formData.append('Comments', this.comments)
      formData.append('CompanyName', this.brandName)
      formData.append('CompanyType', this.customerType)
      formData.append('ClientPartner', this.isMember)

      axios
        .post(this.configuration.endpointSubmitData, formData)
        .then((response) => {
          const { result, description } = response.data
          // response has error
          if (result === 'error') {
            this.handleError([description])
            return false
          }

          this.modalSuccess.show()
          this.resetForm()
          this.handleSuccess()
        })
        .catch((errors) => {
          this.handleError([this.dictionary.defaultErrorMessage])
          console.error(errors)
        })
    },

    handleSuccess() {
      this.isLoading = false
      this.errorMsgs = []
    },

    handleError(messages) {
      this.errorMsgs = messages
      this.isLoading = false
      this.resetReCaptcha()
      this.$nextTick(() => {
        lib.scrollToError(100)
      })
    },
  },
}
</script>
